import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Div } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { appActions, onboardingActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { ROUTES } from 'src/config';
import { Switch } from 'src/components';

import Actuarial from './actuarial_model';
import Classifier from './classifier_model';
import PredictForward from './predict_forward';
import Reonboarding from './reonboarding';

const getTagStyles = (tag) => {
  switch (tag) {
    case 'MISS':
      return {
        name: 'Negative',
        color: 'tagMiss',
        viewBox: '0 0 50 50',
      };
    case 'VALIDATION':
      return {
        name: 'Security',
        color: 'tagValidation',
        viewBox: '0 0 10.29 12',
      };
    case 'TARGET':
      return {
        name: 'TargetNew',
        color: 'tagTarget',
        viewBox: '0 0 50 50',
      };
    case 'ACTUARIAL':
      return {
        name: 'ActuarialModel',
        color: 'tagActuarial',
        viewBox: '0 0 12 12',
      };
    case 'HIT':
    default:
      return {
        name: 'Positive',
        color: 'tagHit',
        viewBox: '0 0 50 50',
      };
  }
};

const getOnboardingType = (client, onboardingConfig) => {
  let onboardingType = CONSTANTS.ONBOARDING_TYPES.ONBOARDING;
  if (client.canEnrich || onboardingConfig.show_enrichment) {
    onboardingType = CONSTANTS.ONBOARDING_TYPES.ENRICHMENT;
  }
  if (CONSTANTS.FL_LIT_MODEL_TENANTS.includes(client.tenant)) {
    onboardingType = CONSTANTS.ONBOARDING_TYPES.FLORIDA_LITIGATION_MODEL;
  }
  return onboardingType;
};

/* eslint-disable react/prop-types */

function NewOnboarding({
  model,
  client,
  clientOnboardingConfig,
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  setDatasetName,
  setDatasetTag,
  uploadFiles,
  setCompState,
  onDone,
  goBack,
}) {
  return (
    <Switch status={model}>
      <Div value={CONSTANTS.ONBOARDING_MODELS.ACTUARIAL_MODEL}>
        {(client?.canModel || clientOnboardingConfig?.show_modeling) && (
          <Actuarial
            datasetTag={datasetTag}
            handleFileDrop={handleFileDrop}
            handleFileSelect={handleFileSelect}
            files={files}
            stageFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
            datasetName={datasetName}
            isAdmin={isAdmin}
            triggerDetWorkflowStatus={triggerDetWorkflowStatus}
            getTagStyles={getTagStyles}
            setDatasetName={setDatasetName}
            setDatasetTag={setDatasetTag}
            compState={compState}
            uploadFiles={uploadFiles}
            setCompState={setCompState}
            onDone={onDone}
            goBack={goBack}
          />
        )}
      </Div>
      <Div value={CONSTANTS.ONBOARDING_MODELS.CLASSIFIER_MODEL}>
        {(client?.canModel || clientOnboardingConfig?.show_modeling) && (
          <Classifier
            datasetTag={datasetTag}
            handleFileDrop={handleFileDrop}
            handleFileSelect={handleFileSelect}
            files={files}
            stageFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
            datasetName={datasetName}
            isAdmin={isAdmin}
            triggerDetWorkflowStatus={triggerDetWorkflowStatus}
            getTagStyles={getTagStyles}
            setDatasetName={setDatasetName}
            setDatasetTag={setDatasetTag}
            compState={compState}
            uploadFiles={uploadFiles}
            setCompState={setCompState}
            onDone={onDone}
            goBack={goBack}
          />
        )}
      </Div>
      <Div value={CONSTANTS.ONBOARDING_MODELS.PREDICT_MODEL}>
        {client?.canScore && (
          <PredictForward
            datasetTag={datasetTag}
            handleFileDrop={handleFileDrop}
            handleFileSelect={handleFileSelect}
            files={files}
            stageFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
            datasetName={datasetName}
            isAdmin={isAdmin}
            triggerDetWorkflowStatus={triggerDetWorkflowStatus}
            getTagStyles={getTagStyles}
            setDatasetName={setDatasetName}
            setDatasetTag={setDatasetTag}
            compState={compState}
            uploadFiles={uploadFiles}
            setCompState={setCompState}
            onDone={onDone}
            goBack={goBack}
          />
        )}
      </Div>
    </Switch>
  );
}

/* eslint-enable react/prop-types */

function Onboarding({
  history,
  app: { profile, tenant },
  onboarding: {
    datasetName,
    datasetTag,
    files,
    progress,
    uploadFileStatus,
    uploadFileError,
    triggerDetWorkflowStatus,
    triggerDetWorkflowError,
    bucket,
    key,
    client,
    clientOnboardingConfig,
    fetchClientByTenantStatus,
    fetchClientOnboardingConfigStatus,
  },
  fetchClientByTenant,
  fetchClientOnboardingConfig,
  stageFiles,
  setDatasetName,
  setDatasetTag,
  uploadFiles,
  triggerDetWorkflow,
  enqueueNotification,
  flashMessage,
  hideMessage,
}) {
  const [compState, setCompState] = useState(CONSTANTS.COMPONENT.VALIDATION.PENDING);
  const location = useLocation();
  const [model, setModel] = useState();
  const [isReonboarding, setReonboarding] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    profile.role === CONSTANTS.ROLE.ADMIN || profile.siteRole === CONSTANTS.ROLE.SUPER_ADMIN
  );

  const handleFileSelect = useCallback(
    (_files) => {
      if (_files.length > 0) {
        stageFiles([_files[0]]);
      }
    },
    [stageFiles]
  );

  const initialize = () => {
    stageFiles([]);
    setDatasetName('');
    setCompState(CONSTANTS.COMPONENT.VALIDATION.PENDING);
    if (location?.state?.datasetTag) {
      setDatasetTag(location.state.datasetTag);
    } else {
      setDatasetTag(CONSTANTS.SEGMENT.TAG.HIT);
    }
    if (location?.state?.model) {
      setModel(location.state.model);
    }
    if (
      location?.state?.datasetName &&
      location?.state?.onboardingType &&
      location?.state?.onboardingPayload &&
      location?.state?.onboardingId
    ) {
      setDatasetName(location.state.datasetName);
      setReonboarding(true);
    }
  };

  const validate = () => {
    if (client && clientOnboardingConfig) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
    }
    if (
      fetchClientByTenantStatus === CONSTANTS.API.FAILED ||
      fetchClientOnboardingConfigStatus === CONSTANTS.API.FAILED
    ) {
      setCompState(CONSTANTS.COMPONENT.VALIDATION.FAILED);
    }
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (tenant) {
      fetchClientByTenant(tenant);
      fetchClientOnboardingConfig('ONBOARDING', null, tenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  useEffect(() => {
    setIsAdmin(profile.role === CONSTANTS.ROLE.ADMIN || profile.siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
  }, [profile.role, profile.siteRole]);

  const handleFileDrop = useCallback((item, monitor) => {
    if (monitor) {
      const { files: _files } = monitor.getItem();
      stageFiles([_files[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchClientByTenantStatus, fetchClientOnboardingConfigStatus, client, clientOnboardingConfig]);

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.STARTED) {
      flashMessage('Please wait. We are validating your file format.', { loading: true });
    } else {
      if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) {
        setCompState(CONSTANTS.COMPONENT.OPERATION.SUCCEEDED);
        hideMessage();
        setTimeout(() => {
          stageFiles([]);
          setDatasetName('');
          setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
          enqueueNotification('Your file has been successfully uploaded.');
          if (isReonboarding) {
            history.push(ROUTES.ONBOARDING);
          }
        }, 500);
      }
      if (triggerDetWorkflowStatus === CONSTANTS.API.FAILED) {
        setCompState(CONSTANTS.COMPONENT.OPERATION.FAILED);
        hideMessage();
        const errorMessage = triggerDetWorkflowError
          ? `Your onboarding has failed. ${triggerDetWorkflowError}`
          : 'There was an error while onboarding the file';
        enqueueNotification(errorMessage, { autoClose: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerDetWorkflowStatus, triggerDetWorkflowError]);

  useEffect(() => {
    if (uploadFileStatus === CONSTANTS.API.SUCCEEDED) {
      const onboardingType = location?.state?.onboardingType || getOnboardingType(client, clientOnboardingConfig);
      const payload = location?.state?.onboardingPayload
        ? {
            bucket,
            key,
            payload: location.state.onboardingPayload,
            onboarding_id: location.state.onboardingId,
          }
        : {
            bucket,
            key,
            payload: {
              profile,
              datasetName,
              datasetTag,
              onboardingType,
            },
          };
      // Since the uploadFileStatus are preserved for 500 ms before
      // being reset, we delay trigering the workflow for 1000 seconds
      // as aa safeguard to prevent multiple uploads
      setTimeout(() => {
        triggerDetWorkflow(payload);
      }, 1000);
    }
    if (uploadFileStatus === CONSTANTS.API.FAILED) {
      setCompState(CONSTANTS.COMPONENT.OPERATION.FAILED);
      enqueueNotification(`There was an error while onboarding the file: ${uploadFileError}`, { autoClose: false });
      setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileStatus, uploadFileError]);

  return (
    <Div p={{ t: '72px' }} w="788px" m="auto">
      {isReonboarding ? (
        <Reonboarding
          datasetTag={datasetTag}
          handleFileDrop={handleFileDrop}
          handleFileSelect={handleFileSelect}
          files={files}
          stageFiles={stageFiles}
          progress={progress}
          uploadFileStatus={uploadFileStatus}
          datasetName={datasetName}
          isAdmin={isAdmin}
          triggerDetWorkflowStatus={triggerDetWorkflowStatus}
          getTagStyles={getTagStyles}
          setDatasetName={setDatasetName}
          setDatasetTag={setDatasetTag}
          compState={compState}
          uploadFiles={uploadFiles}
          setCompState={setCompState}
          onDone={() => {
            history.push(ROUTES.ONBOARDING_LANDING);
          }}
          goBack={() => {
            history.goBack();
          }}
        />
      ) : (
        <NewOnboarding
          model={model}
          client={client}
          clientOnboardingConfig={clientOnboardingConfig}
          datasetTag={datasetTag}
          handleFileDrop={handleFileDrop}
          handleFileSelect={handleFileSelect}
          files={files}
          stageFiles={stageFiles}
          progress={progress}
          uploadFileStatus={uploadFileStatus}
          datasetName={datasetName}
          isAdmin={isAdmin}
          triggerDetWorkflowStatus={triggerDetWorkflowStatus}
          getTagStyles={getTagStyles}
          setDatasetName={setDatasetName}
          setDatasetTag={setDatasetTag}
          compState={compState}
          uploadFiles={uploadFiles}
          setCompState={setCompState}
          onDone={() => {
            history.push(ROUTES.ONBOARDING);
          }}
          goBack={() => {
            history.goBack();
          }}
        />
      )}
    </Div>
  );
}

const mapStateToProps = ({ app, onboarding }) => ({
  app,
  onboarding,
});

const mapDispatchToProps = (dispatch) => ({
  fetchClientByTenant: (...params) => dispatch(onboardingActions.fetchClientByTenant(...params)),
  fetchClientOnboardingConfig: (...params) => dispatch(onboardingActions.fetchClientOnboardingConfig(...params)),
  stageFiles: (files) => dispatch(onboardingActions.stageFiles(files)),
  setDatasetName: (...params) => dispatch(onboardingActions.setDatasetName(...params)),
  setDatasetTag: (...params) => dispatch(onboardingActions.setDatasetTag(...params)),
  uploadFiles: (...params) => dispatch(onboardingActions.uploadFiles(...params)),
  triggerDetWorkflow: (...params) => dispatch(onboardingActions.triggerDetWorkflow(...params)),
  enqueueNotification: (message) => dispatch(appActions.enqueueNotification(message)),
  flashMessage: (...params) => dispatch(appActions.flashMessage(...params)),
  hideMessage: (...params) => dispatch(appActions.hideMessage(...params)),
});

Onboarding.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string,
    profile: PropTypes.shape({
      role: PropTypes.string,
      siteRole: PropTypes.string,
    }),
  }).isRequired,
  onboarding: PropTypes.shape({
    files: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    datasetName: PropTypes.string.isRequired,
    datasetTag: PropTypes.string.isRequired,
    progress: PropTypes.arrayOf(PropTypes.number).isRequired,
    uploadFileStatus: PropTypes.string.isRequired,
    uploadFileError: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
    triggerDetWorkflowStatus: PropTypes.string,
    triggerDetWorkflowError: PropTypes.string,
    bucket: PropTypes.string,
    key: PropTypes.string,
    client: PropTypes.shape({
      canModel: PropTypes.bool,
      canScore: PropTypes.bool,
    }),
    clientOnboardingConfig: PropTypes.shape({
      show_enrichment: PropTypes.bool,
      show_modeling: PropTypes.bool,
    }),
    fetchClientByTenantStatus: PropTypes.string,
    fetchClientOnboardingConfigStatus: PropTypes.string,
  }).isRequired,
  fetchClientByTenant: PropTypes.func.isRequired,
  fetchClientOnboardingConfig: PropTypes.func.isRequired,
  stageFiles: PropTypes.func.isRequired,
  uploadFiles: PropTypes.func.isRequired,
  setDatasetName: PropTypes.func.isRequired,
  setDatasetTag: PropTypes.func.isRequired,
  triggerDetWorkflow: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  flashMessage: PropTypes.func.isRequired,
  hideMessage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Onboarding));
